import {EventEmitter, Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {Parcel, Resident} from '../types/models';
import {UrlService} from './url.service';
import {EmptyRowService} from './empty-row.service';
import {ConstantsService} from './constants.service';
import {AccountPermissionsService} from './account-permissions.service';
import {ResidentService} from './resident.service';
import {AuthenticationService} from './authentication.service';
import {Observable} from 'rxjs/Observable';
import {catchError, filter, map, tap} from 'rxjs/operators';
import 'rxjs/add/operator/do';
import {HistoryItem} from '../views/directory-item/activity/history-item.class';
import {createInitialNetworkEntity, resolveNetworkEntity} from './helper-functions';
import {IBuilding} from '../types/api-response/building';

declare const toastr: any;
declare const swal: any;
declare const $: any;
declare const moment: any;

@Injectable()
export class ParcelsService {
    isParcelModalShow = false;
    isTransitModalShow = false;
    isParcelHistoryModalShow = false;
    isParcelReminderModalShow = false;
    isEnterParcelBarcodeModalShow = false;
    showDetailedView = false;

    parcelInParcelModal: Parcel;
    parcelInHistoryModal: Parcel;
    parcelInTransitModal: Parcel;
    statusInTransitModal: string;

    data = {
        allParcels: {
            get: '/v1/parcels.json?expand=building,suite,resident,receivedBy,pickedUpBy,deliveredBy,parcelHistory,pickedUpByPostService,parcelType,parcelPostService,cancelledBy',
            data: [],
            total: null
        },
    };
    tabId = 'allParcels';
    spinnerShow = true;
    showServiceUnavailable = false;
    parcelTypes: Array<any> = [];
    postServices: Array<any> = [];
    buildings: Array<IBuilding | any> = [];
    buildingsForRead: Array<any> = [];
    buildingsForUpdate: Array<any> = [];
    serviceName: string = 'parcels';
    isMassParcelModalShow = false;
    selectedItemData: Parcel;
    activities = [];
    historyLoading = false;
    showImageCaptureModal = false;
    imageCaptured: EventEmitter<any> = new EventEmitter();

    constructor(public api: ApiClientService,
                public urlService: UrlService,
                public emptyRowService: EmptyRowService,
                public account: AccountPermissionsService,
                private residentService: ResidentService,
                private authenticationService: AuthenticationService
    ) {
        this.api.onLogout.subscribe(() => this.buildings = []);
    }

    getData(scrollToTop?) {
        this.getDataByQueryParams(scrollToTop).subscribe();
    }

    getDataByQueryParams(scrollToTop?) {
        this.showServiceUnavailable = false;
        this.data[this.tabId].total = null;

        const tabId = this.urlService.queryParams['tab'];
        const url = this.data[tabId].get;
        const queryParams = this.urlService.getQueryParamsString();
        const selectedBuilding = JSON.parse(localStorage.getItem('residentselectedBuilding'));
        let sortedByBuilding = '';
        if (this.authenticationService.userIsResident && selectedBuilding) {
            sortedByBuilding = `&building_id=${selectedBuilding.buildingId}&suite_id=${selectedBuilding.suite_id}`;
        }
        return this.api.getJson('domainParcels', `${url}${queryParams}${this.authenticationService.userIsResident ? sortedByBuilding : ''}`)
            .pipe(
                createInitialNetworkEntity(),
                resolveNetworkEntity((item) => {
                    if (item.building && item.building.bdId) item.building.id = item.building.bdId;
                    return Parcel.fromExistingData(this.account.addPermissionsToEntity(item, this.serviceName));
                }),
                tap(({finished}) => this.spinnerShow = !finished),
                tap(
                    (body) => {
                        this.data[tabId].data = body.entities;
                        this.data[tabId].total = body.total;
                        this.emptyRowService.fillEmptyRow();
                        if (scrollToTop && $('.table')[0])
                            $('.table')[0].scrollIntoView();
                        if (this.data.allParcels.data.length == 1 && this.data.allParcels.data[0].status == 'received' &&
                            this.urlService.queryParams.query && this.urlService.queryParams.query.indexOf('barCode') != -1 &&
                            /\*(.*)\*$/g.exec(this.urlService.queryParams.query) &&
                            `*${this.data.allParcels.data[0].barCode}*` === /\*(.*)\*$/g.exec(this.urlService.queryParams.query)[0])
                            this.showTransitModal(this.data.allParcels.data[0], 'pick_up');
                    },
                    (error) => {
                        this.showServiceUnavailable = true;
                        this.spinnerShow = false;
                        this.api.errorHandler(error, 'Failed get Parcels');
                    }
                ),
                filter(({finished}) => finished),
                tap(() => {
                    if (this.selectedItemData) {
                        this.selectedItemData = this.data.allParcels.data.find(si => si.id == this.selectedItemData.id);
                        if (this.selectedItemData)
                            this.selectedItemData.showDetailedView = true;
                        else
                            this.showDetailedView = false;
                    }
                }),
            );
    }

    parcelsFactory(entities: any, targetArray) {
        entities.forEach((item) => {
            const parcel = Parcel.fromExistingData(item);
            targetArray.push(parcel);
        });
    }

    getParcelTypes(): Observable<any> {
        const url = `/v1/parcel-types.json?per-page=1000`;
        return this.api.getJson('domainParcels', url)
            .pipe(
                tap(
                    (res) => this.parcelTypes = res.entities,
                    (error) => this.api.errorHandler(error, 'Failed get parcel types')
                )
            );
    }

    getPostServices(): Observable<any> {
        const url = `/v1/post-services.json?per-page=1000`;
        return this.api.getJson('domainParcels', url)
            .pipe(
                tap(
                    (res) => this.postServices = res.entities,
                    (error) => this.api.errorHandler(error, 'Failed get post-services')
                )
            );
    }

    getBuildings(): void {
        const url = `/v1/buildings/short.json?expand=timeZone&order-by=name|asc`;
        this.api.getInfiniteList('domainBD', url, 250).subscribe(
            (result) => {
                this.buildings = result.entities;
                this.getFilteredBuildings();
            }, (error) => this.api.errorHandler(error, 'Failed get buildings')
        );
    }

    resetData() {
        for (const item in this.data) {
            this.data[item].data = [];
            this.data[item].total = null;
        }
    }

    editParcel(parcel: Parcel) {
        this.isParcelModalShow = true;
        this.parcelInParcelModal = Object.assign({}, parcel);
    }

    remove(id: number): void {
        if (!id) return;
        swal(ConstantsService.getSwalConfig('Delete parcel'),
            () => {
                const url = `/v1/parcels/${id}`;
                this.api.remove('domainParcels', url).subscribe(
                    () => {
                        toastr.success('Parcel has been deleted.');
                        if (this.data.allParcels.data.length - 1 == 0 && this.urlService.queryParams.page != '1')
                            this.urlService.queryParams.page = (parseInt(this.urlService.queryParams.page) - 1).toString();
                        this.getData();
                    },
                    (error) => this.api.errorHandler(error, 'Delete data failed')
                );
            }
        );
    }

    /**
     * Adds email to the profile
     * for selected resident
     *
     * @param suiteUserId - Id of SuiteUser relation object
     * @param email
     */
    public setResidentEmail(suiteUserId, email): Observable<Resident> {
        return this.api.patch('domainBD', `/v1/suite-users/${suiteUserId}.json?expand=user`, {user: {email}})
            .pipe(
                tap(() => toastr.success('Resident was updated!')),
                catchError(err => {
                    this.api.errorHandler(err, 'Failed to update user profile.');
                    throw err;
                }),
                map(suiteUser => suiteUser.user),
            );
    }

    showHistory(parcel: Parcel) {
        this.isParcelHistoryModalShow = true;
        this.parcelInHistoryModal = parcel;
    }

    showTransitModal(parcel: Parcel, status: string) {
        this.parcelInTransitModal = parcel;
        this.statusInTransitModal = status;
        this.isTransitModalShow = true;
    }

    getFilteredBuildings() {
        this.buildingsForRead = this.account.getBuildingsForRead(this.buildings, this.serviceName);
        this.buildingsForUpdate = this.account.getBuildingsForUpdate(this.buildings, this.serviceName);
    }

    sendReminder(parcel: Parcel) {
        const url = `/v1/parcels/${parcel.id}/remind.json`;
        this.api.post('domainParcels', url, {}).subscribe(
            res => {
                parcel.reminderSentAt = new Date().toISOString();
                toastr.success('Reminder has been sent successfully.');
            },
            error => this.api.errorHandler(error, 'Failed to send reminder.'),
        );
    }

    toggleDetailedView(data, state: boolean): void {
        this.data.allParcels.data.forEach(item => item.showDetailedView = false);
        this.selectedItemData = data ? data : null;
        this.showDetailedView = state;
        if (this.selectedItemData) {
            this.selectedItemData.showDetailedView = true;
            this.getActivity();
            for (const field in this.selectedItemData.formFields)
                this.selectedItemData.formFields[field].editMode = false;
        }
    }

    getActivity() {
        if (this.selectedItemData) {
            this.historyLoading = true;
            const url = `/v1/history.json?per-page=10000&order-by=loggedAt|desc&objectId=${this.selectedItemData.id}`;
            this.api.get('domainParcels', url).subscribe(
                res => {
                    this.historyLoading = false;
                    this.activities = res.json().map(a => new HistoryItem(a));
                },
                error => {
                    this.historyLoading = false;
                    this.api.errorHandler(error, 'Failed get Parcels');
                }
            );
        }
    }

    printParcel(parcel): void {
        const timezone = parcel.building.timeZone ? parcel.building.timeZone : 'America/Toronto';
        const firstPart = '<!DOCTYPE html>\n' +
            '<html>\n' +
            '    <head>\n' +
            '        <style>\n' +
            '            table{ \n' +
            '                max-width: 770px;\n' +
            '                max-height: 265px; \n' +
            '                border: 4px solid black;\n' +
            '                border-spatimezonecing: 0;\n' +
            '                border-collapse: collapse;\n' +
            '                font-family: Arial;\n' +
            '            }\n' +
            '            th{ \n' +
            '                border-bottom: 1px solid black;\n' +
            '                font-size: 19px;\n' +
            '                padding: 0 10px\n' +
            '            }\n' +
            '            td{ \n' +
            '                border: 1px solid black;\n' +
            '                padding: 0 10px;\n' +
            '                vertical-align: top;' +
            '                font-size: 20px;' +
            '            }\n' +
            '            .status{ text-transform: capitalize; }\n' +
            '            .signature{ max-width: 240px; }\n' +
            '            .main-wrapper{' +
            '                position: absolute;\n' +
            '                top: 0;' +
            '            }' +
            '        </style>\n' +
            '        <title>Parking Pass</title>\n' +
            '        <meta charset="UTF-8">\n' +
            '    </head>\n' +
            '    <body>\n' +
            '        <div class="main-wrapper">\n';
        const template =
            '            <table>\n' +
            '                <tr>\n' +
            '                    <td>\n' +
            '                        Building Name :\n' +
            '                        <br>\n' +
            `                        <b>${parcel.buildingName || '-'}</b>\n` +
            '                    </td>\n' +
            '                    <td>\n' +
            '                        Pieces \n' +
            '                        <br>\n' +
            `                        <b>${parcel.numberPieces || '-'}</b>\n` +
            '                    </td>\n' +
            '                    <td>\n' +
            '                        Emailed to Resident \n' +
            '                        <br>\n' +
            `                        <b>${parcel.emailNotice ? 'Yes' : 'No'}</b>\n` +
            '                    </td>\n' +
            '                </tr>\n' +
            '                <tr>\n' +
            '                    <td>\n' +
            '                        Unit Number:\n' +
            '                        <br>\n' +
            `                        <b>${parcel.suiteNumber || '-'}</b>\n` +
            '                    </td>\n' +
            '                    <td colspan="2">\n' +
            '                        Barcode \n' +
            '                        <br>\n' +
            `                        <b>${parcel.barCode || '-'}</b>\n` +
            '                    </td>\n' +
            '                </tr>\n' +
            '                <tr>\n' +
            '                    <td>\n' +
            '                        Resident \n' +
            '                        <br>\n' +
            `                        <b>${parcel.residentFullName || '-'}</b>\n` +
            '                    </td>\n' +
            '                    <td colspan="2">\n' +
            '                        Courier \n' +
            '                        <br>\n' +
            `                        <b>${parcel.parcelPostServiceName || '-'}</b>\n` +
            '                    </td>\n' +
            '                </tr>\n' +
            '                <tr>\n' +
            '                    <td>\n' +
            '                        Status \n' +
            '                        <br>\n' +
            `                        <b class="status">${this.getStatusName(parcel.status)}</b>\n` +
            '                    </td>\n' +
            '                    <td colspan="2">\n' +
            '                        Date Received \n' +
            '                        <br>\n' +
            `                        <b>${moment.utc(parcel.createdAt).tz(timezone).format('YYYY-MM-DD hh:mm A')}</b>\n` +
            '                    </td>\n' +
            '                </tr>\n' +
            '                <tr>\n' +
            '                    <td>\n' +
            '                        Package Type \n' +
            '                        <br>\n' +
            `                        <b>${parcel.parcelTypeName || '-'}</b>\n` +
            '                    </td>\n' +
            '                    <td colspan="2">\n' +
            '                        Date Picked Up \n' +
            '                        <br>\n' +
            `                        <b>${parcel.pickedUpAt ? moment.utc(parcel.pickedUpAt).tz(timezone).format('YYYY-MM-DD hh:mm A') : '-'}</b>\n` +
            '                    </td>\n' +
            '                </tr>\n' +
            '            </table>\n';
        const secondPart =
            '        </div>\n' +
            '    </body>\n' +
            '</html>';
        const tab = window.open();
        tab.document.write(firstPart + template + secondPart);
        tab.stop();
        tab.print();
        tab.close();
    }

    getStatusName(status) {
        if (status != 'picked_up')
            return status;
        else
            return 'Picked Up';
    }

    onImageCaptureModalClose(eventData) {
        this.showImageCaptureModal = false;
        if (eventData)
            this.imageCaptured.emit(eventData);
    }

    getParcelByBarcode(barcode) {
        const url = `/v1/parcels.json?query=${barcode}&expand=building,suite,resident,receivedBy,pickedUpBy,deliveredBy,parcelHistory,pickedUpByPostService,parcelType,parcelPostService,cancelledBy`;
        return this.api.get('domainParcels', url);
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/parcels.service.ts